import { useMemo } from 'react';

import { useGetConversationsByParentId } from '@/hooks/queries';
import { toLocalDateTime } from '@/utils/dateUtils';

export interface RequestHistoryEntry {
  id: string;
  authorName: string | null;
  content: string;
  timestamp: string;
}

interface UseResponseRequestHistoryResult {
  entries: RequestHistoryEntry[];
  loading: boolean;
}

/**
 * Flattens the response's threads into one chronological list of requests
 * (CUS-243).
 *
 * Flattening rather than rendering per-thread is deliberate: a response has a
 * single thread in practice (the writer find-or-creates the oldest one), and
 * the AC asks for "all prior request comments in chronological order" — one
 * list, not a set of nested groups. Flattening also degrades sensibly if a
 * second thread ever appears on the node from another source.
 *
 * Ordered NEWEST FIRST. The reviewer opening this wants the request they or a
 * colleague just sent, not the first one ever raised; on a long thread the
 * latest would otherwise sit below the fold.
 *
 * Both levels arrive oldest-first from the data layer, so this sort is not a
 * no-op — and it is over the FLATTENED list, which interleaving two threads
 * would otherwise leave out of order either way.
 */
/**
 * Newest first. Mirrors `byCreatedAtAscending` in the data layer's conversation
 * repository, inverted.
 *
 * `Date.parse` alone is not enough: it truncates to milliseconds while
 * `statement_timestamp()` is microsecond, so two comments written in the same
 * millisecond compare equal and the stable sort quietly falls back to thread
 * order. The string tiebreak restores the finer ordering.
 *
 * Comparing the strings alone would be wrong the other way: a timestamp landing
 * exactly on a second carries no fractional digits at all, and `'Z'` sorts after
 * `'.'`, which would put it after a later same-second value.
 */
const byCreatedAtDescending = (
  a: { createdAt: string },
  b: { createdAt: string }
): number => {
  const difference = Date.parse(b.createdAt) - Date.parse(a.createdAt);

  if (difference !== 0 && !Number.isNaN(difference)) {
    return difference;
  }

  return b.createdAt.localeCompare(a.createdAt);
};

export const useResponseRequestHistory = (
  responseId?: string | null
): UseResponseRequestHistoryResult => {
  const { conversations, loading } = useGetConversationsByParentId(responseId);

  const entries = useMemo(
    () =>
      conversations
        .flatMap((conversation) => conversation.comments)
        .map((comment) => ({
          id: comment.Id,
          authorName: comment.createdByUser?.FriendlyName ?? null,
          content: comment.Content,
          timestamp: toLocalDateTime(comment.CreatedAtTimestamp),
          createdAt: comment.CreatedAtTimestamp,
        }))
        .sort(byCreatedAtDescending)
        .map(({ createdAt: _createdAt, ...entry }) => entry),
    [conversations]
  );

  return { entries, loading };
};
